import React, { useEffect, useRef, useState } from 'react';
import '../../styles/Objects.css';

interface AlphaLoopProps {
  /** basename shared by public/videos/objects/{slug}.webm and its WebP still */
  slug: string;
  className?: string;
}

/**
 * Transparent VP9 video loop with a WebP-still escape hatch.
 * Falls back to the still when: the browser won't take VP9 webm at all,
 * the decoder renders it opaque (Safari/iOS; probed by drawing a frame and
 * reading a corner pixel's alpha), playback errors, or the user prefers
 * reduced motion. Plays only while on screen.
 */
const AlphaLoop: React.FC<AlphaLoopProps> = ({ slug, className = '' }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [mode, setMode] = useState<'video' | 'still'>(() => {
    if (typeof window === 'undefined') return 'still';
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 'still';
    const probe = document.createElement('video');
    return probe.canPlayType('video/webm; codecs="vp9"') ? 'video' : 'still';
  });

  useEffect(() => {
    if (mode !== 'video') return;
    const video = videoRef.current;
    if (!video) return;
    video.muted = true; // belt and braces for autoplay policy

    const probeAlpha = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = canvas.height = 8;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(video, 0, 0, 8, 8);
        // objects are centered, so a corner pixel is transparent in every
        // frame; an opaque read means the decoder dropped the alpha plane
        if (ctx.getImageData(1, 1, 1, 1).data[3] === 255) setMode('still');
      } catch {
        /* canvas unavailable; leave the video in place */
      }
    };
    const fail = () => setMode('still');

    video.addEventListener('loadeddata', probeAlpha, { once: true });
    video.addEventListener('error', fail);
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) video.play().catch(fail);
        else video.pause();
      },
      { threshold: 0.25 }
    );
    io.observe(video);

    return () => {
      io.disconnect();
      video.removeEventListener('loadeddata', probeAlpha);
      video.removeEventListener('error', fail);
    };
  }, [mode]);

  if (mode === 'still') {
    return (
      <img
        src={`/images/objects/${slug}.webp`}
        alt=""
        aria-hidden="true"
        width={800}
        height={800}
        loading="lazy"
        decoding="async"
        draggable={false}
        className={`obj obj-float ${className}`}
      />
    );
  }

  return (
    <video
      ref={videoRef}
      className={`obj ${className}`}
      width={720}
      height={720}
      muted
      loop
      playsInline
      preload="metadata"
      poster={`/images/objects/${slug}.webp`}
      aria-hidden="true"
      tabIndex={-1}
    >
      <source src={`/videos/objects/${slug}.webm`} type='video/webm; codecs="vp9"' />
    </video>
  );
};

export default AlphaLoop;
